import { NavLink, useNavigate } from "react-router-dom";
import { Home, Layers, Dumbbell, Salad, CheckCircle2, LineChart, MessageCircle, Users, BookOpen, Sparkles, LogOut, Trophy, ChefHat, Moon, CalendarDays } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BRAND } from "@/lib/brand";

const NAV = [
  { to: "/app", label: "Today", icon: Home, end: true },
  { to: "/app/program", label: "MTM", icon: Layers, full: true },
  { to: "/app/workouts", label: "Workouts", icon: Dumbbell },
  { to: "/app/nutrition", label: "Nutrition", icon: Salad },
  { to: "/app/recipes", label: "Recipes", icon: ChefHat },
  { to: "/app/planner", label: "Planner", icon: CalendarDays },
  { to: "/app/cycle", label: "Cycle", icon: Moon },
  { to: "/app/habits", label: "Rituals", icon: CheckCircle2 },
  { to: "/app/progress", label: "Progress", icon: LineChart },
  { to: "/app/wins", label: "Wins", icon: Trophy },
  { to: "/app/messages", label: "Coach", icon: MessageCircle, full: true },
  { to: "/app/forums", label: "Community", icon: Users },
  { to: "/app/resources", label: "Library", icon: BookOpen, full: true },
  { to: "/app/ai", label: "AI Coach", icon: Sparkles, full: true },
];

export function ClientLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMembership = (user?.tier || "mtm") === "membership";
  const nav = isMembership ? NAV.filter((n) => !n.full) : NAV;
  return (
    <div className="min-h-screen bg-app pb-24 lg:pb-0">
      <aside className="fixed inset-y-0 left-0 w-64 border-r flex-col py-8 px-4 hidden lg:flex"
        style={{ background: "var(--surface-1)", borderColor: "var(--border)" }}>
        <div className="px-3 mb-10">
          <img src="https://customer-assets.emergentagent.com/job_coach-connect-hub-2/artifacts/yotpn0t5_White%20logo%20WW.png.png"
               alt="Wellness Way with DJ" className="w-28 h-28 object-contain mb-2"
               style={{ filter: "var(--logo-filter, none)", mixBlendMode: "var(--logo-blend, normal)" }} />
          <div className="bronze-rule mt-2" />
          <div className="font-soft text-base mt-4" style={{ color: "var(--text-secondary)" }}>
            Hello, <span style={{ color: "var(--text-primary)" }}>{user?.name?.split(" ")[0] || "friend"}</span>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              data-testid={`client-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              <Icon size={14} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto pt-6 border-t space-y-2" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center justify-between px-2">
            <span className="font-label text-[10px] tracking-wider truncate" style={{ color: "var(--text-secondary)" }}>{user?.email}</span>
            <ThemeToggle />
          </div>
          <button type="button" data-testid="client-logout"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="nav-link w-full">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <main className="lg:pl-64">
        <div className="px-5 md:px-10 py-8 max-w-[1200px] mx-auto">{children}</div>
      </main>
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 backdrop-blur-xl border-b px-4 py-3 flex items-center justify-between"
        style={{ background: "rgba(255,255,255,0.85)", borderColor: "var(--border)" }}>
        <div className="flex items-baseline gap-2">
          <span className="font-script text-xl">{BRAND.name}</span>
          <span className="wordmark-tag text-[9px]">{BRAND.tag}</span>
        </div>
        <ThemeToggle />
      </div>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t"
        style={{ background: "var(--surface-1)", borderColor: "var(--border)" }}>
        <div className="grid grid-cols-5 gap-1 px-1 py-2">
          {nav.slice(0, 5).map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-1 font-label text-[9px] uppercase tracking-[0.18em] ${
                  isActive ? "text-accent" : ""
                }`
              }
              style={({ isActive }) => ({ color: isActive ? "var(--accent)" : "var(--text-secondary)" })}>
              <Icon size={16} /> {label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
